import ActBadges from '../../../components/ui/ActBadges'
import React, { useMemo, useState } from 'react'
import {
  ACT_FILTER_ALL,
  buildActFilterOptions,
  buildStageBudgetModel,
  formatMoney,
  formatPercent,
  getDepartmentTone,
  getInitials,
} from './stageBudgetUtils'
import { getBudgetExpenseMarker } from '../budgetExpenseVisuals'
import './StageReportPreview.css'

function StageBudgetTable({
  reportData,
  selectedActId = ACT_FILTER_ALL,
  onSelectedActIdChange,
  tableLabel = 'Бюджет стадии',
  // Строка-фильтр «Заактировано» (выбор акта) показывается только когда true.
  // Когда false — фильтр скрыт и всегда отображается срез по всем актам.
  showActFilter = true,
}) {
  // Без фильтра пользователь всегда видит таблицу по всем актам.
  const effectiveSelectedActId = showActFilter ? selectedActId : ACT_FILTER_ALL
  const model = useMemo(() => buildStageBudgetModel(reportData, effectiveSelectedActId), [reportData, effectiveSelectedActId])
  const actFilters = useMemo(() => buildActFilterOptions(reportData?.acts || []), [reportData?.acts])
  const selectedActFilter = actFilters.find((filter) => filter.id === effectiveSelectedActId) || actFilters[0]
  const handleActChange = onSelectedActIdChange || (() => {})
  const [expandedTeamGroups, setExpandedTeamGroups] = useState(() => new Set())

  const toggleTeamGroup = (rowKey) => {
    setExpandedTeamGroups((prev) => {
      const next = new Set(prev)
      if (next.has(rowKey)) next.delete(rowKey)
      else next.add(rowKey)
      return next
    })
  }

  const visibleRows = useMemo(() => model.rows.filter((row) => {
    if (row.type !== 'sub-team') return true
    return expandedTeamGroups.has(row.parentKey)
  }), [expandedTeamGroups, model.rows])

  return (
    <>
      {showActFilter ? (
        <div className="stage-budget-act-filter" aria-label="Фильтр актов стадии">
          <span className="stage-budget-act-filter__label">Заактировано:</span>
          <div className="stage-budget-act-filter__chips">
            {actFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`stage-budget-act-chip${effectiveSelectedActId === filter.id ? ' is-active' : ''}`}
                onClick={() => handleActChange(filter.id)}
              >
                <span>{filter.label}</span>
                <small>{filter.meta}</small>
                {filter.act && <ActBadges act={filter.act} />}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="stage-budget-table-wrap">
        <table
          className={`stage-budget-table${effectiveSelectedActId === ACT_FILTER_ALL ? '' : ' is-act-filtered'}`}
          aria-label={tableLabel}
        >
          <colgroup>
            <col className="stage-budget-table__col-category" />
            <col className="stage-budget-table__col-person" />
            <col className="stage-budget-table__col-amount" />
            <col className="stage-budget-table__col-percent" />
            <col className="stage-budget-table__col-actuated" />
          </colgroup>
          <thead>
            <tr>
              <th>Подразделение</th>
              <th>Исполнитель</th>
              <th className="stage-budget-align-right">По бюджету</th>
              <th className="stage-budget-align-right">% распределения</th>
              <th className="stage-budget-align-right stage-budget-actuated-col">
                <span className="stage-budget-actuated-head">
                  <span>Заактировано</span>
                  {effectiveSelectedActId !== ACT_FILTER_ALL ? (
                    <span className="stage-budget-actuated-head__badge">
                      {selectedActFilter?.headerBadge || selectedActFilter?.label || 'Акт'}
                    </span>
                  ) : null}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => {
              if (row.type === 'section') {
                return (
                  <tr key={row.key} className="stage-budget-row-section">
                    <td colSpan={5}>{row.label}</td>
                  </tr>
                )
              }
              if (row.type === 'section-total') {
                return (
                  <tr key={row.key} className="stage-budget-row-section-total">
                    <td colSpan={2}>{row.label}</td>
                    <td className="stage-budget-align-right">{formatMoney(row.budgetAmount)}</td>
                    <td className="stage-budget-align-right">{formatPercent(row.percentage)}</td>
                    <td className="stage-budget-align-right stage-budget-actuated-col">{formatMoney(row.actuatedAmount)}</td>
                  </tr>
                )
              }
              if (row.type === 'total') {
                return (
                  <tr key={row.key} className="stage-budget-row-total">
                    <td colSpan={2}>{row.label}</td>
                    <td className="stage-budget-align-right">{formatMoney(row.budgetAmount)}</td>
                    <td className="stage-budget-align-right">{formatPercent(row.percentage)}</td>
                    <td className="stage-budget-align-right stage-budget-actuated-col">{formatMoney(row.actuatedAmount)}</td>
                  </tr>
                )
              }
              if (row.type === 'after-tax-total') {
                return (
                  <tr key={row.key} className="stage-budget-row-after-tax-total">
                    <td colSpan={2}>{row.label}</td>
                    <td className="stage-budget-align-right">{formatMoney(row.budgetAmount)}</td>
                    <td className="stage-budget-align-right">{formatPercent(row.percentage)}</td>
                    <td className="stage-budget-align-right stage-budget-actuated-col">{formatMoney(row.actuatedAmount)}</td>
                  </tr>
                )
              }
              const pct = (row.type === 'team' || row.type === 'team-group' || row.type === 'sub-team') && row.percentage == null && model.totalBudget > 0
                ? (row.budgetAmount / model.totalBudget) * 100
                : row.percentage
              const rowClass = [
                `stage-budget-row-${row.type}`,
                row.expenseKind ? `stage-budget-expense--${row.expenseKind}` : '',
              ].filter(Boolean).join(' ')
              const isExpense = row.type === 'expense' || row.type === 'tax'
              const isTeamGroup = row.type === 'team-group'
              const isExpanded = isTeamGroup && expandedTeamGroups.has(row.key)

              return (
                <tr
                  key={row.key}
                  className={rowClass}
                  role={isTeamGroup ? 'button' : undefined}
                  onClick={isTeamGroup ? () => toggleTeamGroup(row.key) : undefined}
                  onKeyDown={isTeamGroup ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggleTeamGroup(row.key)
                    }
                  } : undefined}
                  tabIndex={isTeamGroup ? 0 : undefined}
                  aria-expanded={isTeamGroup ? isExpanded : undefined}
                  aria-label={isTeamGroup ? `${isExpanded ? 'Свернуть' : 'Развернуть'} подкоманды ${row.label}` : undefined}
                >
                  <td>
                    <span className={`stage-budget-category${row.type === 'sub-team' ? ' stage-budget-category--sub-team' : ''}`}>
                      {isTeamGroup ? (
                        <span className={`stage-budget-category__chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden>▸</span>
                      ) : null}
                      <span className={`stage-budget-category__icon ${isExpense ? `stage-budget-category__icon--expense stage-budget-category__icon--expense-${row.expenseKind || 'other'}` : `stage-budget-category__icon--${getDepartmentTone(row.label)}`}`}>
                        {isExpense ? getBudgetExpenseMarker(row.expenseKind) : String(row.label || '—').slice(0, 1).toUpperCase()}
                      </span>
                      <span>{row.label}</span>
                    </span>
                  </td>
                  <td>
                    {row.employeeName ? (
                      <span className={`stage-budget-person${isTeamGroup ? ' stage-budget-person--muted' : ''}`}>
                        {!isTeamGroup ? (
                          <span className="stage-budget-person__avatar">{getInitials(row.employeeName)}</span>
                        ) : null}
                        <span className="stage-budget-person__text">
                          <span>{row.employeeName}</span>
                          {row.teamName ? <small>{row.teamName}</small> : null}
                        </span>
                      </span>
                    ) : null}
                  </td>
                  <td className={`stage-budget-align-right ${row.budgetAmount === 0 ? 'is-zero' : ''}`}>
                    {formatMoney(row.budgetAmount)}
                  </td>
                  <td className="stage-budget-align-right">
                    <span className={pct === 0 ? 'stage-budget-pct is-zero' : 'stage-budget-pct'}>{formatPercent(pct)}</span>
                  </td>
                  <td className={`stage-budget-align-right stage-budget-actuated-col ${row.actuatedAmount === 0 ? 'is-zero' : ''}`}>
                    {formatMoney(row.actuatedAmount)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

export default StageBudgetTable
